import { useMutation } from '@tanstack/react-query';

import { queryClient } from 'api';
import { API_HANDLERS } from 'api/apiHandlers';
import { useActivateToken } from 'api/queries/useActivateToken';
import { QUERIES } from 'constants/index';
import { ENGLISH, SWEDISH } from 'features/languageSelector/components/LanguageSelector/constants';
import { useAuth, useSessionState, useSubscription } from 'hooks';
import { Language } from 'types';

interface UseCompanySwitchMutation {
  reload?: boolean;
}

export function useCompanySwitchMutation({ reload }: UseCompanySwitchMutation) {
  const { saveUser, removeCurrentUser } = useAuth();
  const { addNotification, addMockDataBanner } = useSubscription();
  const { refetch } = useActivateToken();
  const [language, _] = useSessionState('i18nextLng');
  const LANG_CONFIG = language && language === Language.english ? ENGLISH : SWEDISH;

  const { mutateAsync: editUserMutation } = useMutation(API_HANDLERS.USER.EDIT);

  const { mutateAsync: switchCompanyUserMutation, isLoading } = useMutation(
    API_HANDLERS.AUTH.SWITCH_COMPANY,
    {
      onSuccess: async (data) => {
        removeCurrentUser();

        const { token, user } = data.data;

        localStorage.setItem('accessToken', token.access_token);
        localStorage.setItem('refreshToken', token.refresh_token);
        localStorage.setItem('expiresAt', String(token.expires_at));

        queryClient.invalidateQueries([QUERIES.USER_DETAILS, user.id]);

        saveUser({ user });
        addNotification();
        addMockDataBanner();
        if (!localStorage.getItem('webPushToken1')) {
          refetch();
        }

        if (user?.id)
          await editUserMutation({
            id: user.id,
            user: { lang: LANG_CONFIG.value },
            byPassSub: true,
          });

        if (reload) {
          window.location.reload();
        }
      },
    },
  );

  return {
    switchCompanyUserMutation,
    isLoading,
  };
}
